import type { ReactNode } from 'react'
import {
  enumKey,
  initialDraft,
  itemSchema,
  kindOf,
  propertiesOf,
  requiredOf,
  type Draft,
  type FieldErrors,
  type JsonSchema,
} from './schema'

type DraftObject = { [key: string]: Draft }

const domId = (prefix: string, path: string) => `${prefix}-${path}`.replace(/[^a-zA-Z0-9_-]/g, '_')

function FieldShell({
  id,
  label,
  required,
  description,
  error,
  children,
  inline,
}: {
  id: string
  label: string
  required: boolean
  description?: string
  error?: string
  children: ReactNode
  inline?: boolean
}) {
  return (
    <div className={'sf-field' + (inline ? ' sf-field--inline' : '')}>
      {inline ? (
        <label htmlFor={id} className="sf-check">
          {children}
          <span>
            {label}
            {required && <RequiredMark />}
          </span>
        </label>
      ) : (
        <>
          <label htmlFor={id} className="sf-label">
            {label}
            {required && <RequiredMark />}
          </label>
          {children}
        </>
      )}
      {description && <p id={`${id}-desc`} className="sf-help">{description}</p>}
      {error && <p id={`${id}-err`} className="sf-error">{error}</p>}
    </div>
  )
}

function RequiredMark() {
  return (
    <>
      <span className="sf-req" aria-hidden="true"> *</span>
      <span className="sr-only"> (required)</span>
    </>
  )
}

function describedBy(id: string, description?: string, error?: string): string | undefined {
  const ids = [description ? `${id}-desc` : '', error ? `${id}-err` : ''].filter(Boolean)
  return ids.length ? ids.join(' ') : undefined
}

function Field({
  name,
  schema,
  value,
  onChange,
  path,
  required,
  errors,
  idPrefix,
}: {
  name: string
  schema: JsonSchema
  value: Draft
  onChange: (v: Draft) => void
  path: string
  required: boolean
  errors: FieldErrors
  idPrefix: string
}) {
  const id = domId(idPrefix, path)
  const kind = kindOf(schema)
  const label = name
  const description = typeof schema.description === 'string' ? schema.description : undefined
  const error = errors[path]
  const common = {
    id,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': describedBy(id, description, error),
  }

  switch (kind) {
    case 'enum': {
      const options = schema.enum ?? []
      return (
        <FieldShell id={id} label={label} required={required} description={description} error={error}>
          <select {...common} value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value)}>
            <option value="">{required ? 'Select…' : 'Not set'}</option>
            {options.map((o) => (
              <option key={enumKey(o)} value={enumKey(o)}>
                {typeof o === 'string' ? o : JSON.stringify(o)}
              </option>
            ))}
          </select>
        </FieldShell>
      )
    }
    case 'string':
    case 'number':
    case 'integer': {
      const numeric = kind !== 'string'
      const long = kind === 'string' && (schema.format === 'textarea' || (typeof schema.maxLength === 'number' && schema.maxLength > 200))
      return (
        <FieldShell id={id} label={label} required={required} description={description} error={error}>
          {long ? (
            <textarea {...common} rows={4} value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value)} />
          ) : (
            <input
              {...common}
              type={numeric ? 'number' : 'text'}
              step={kind === 'integer' ? 1 : numeric ? 'any' : undefined}
              inputMode={kind === 'integer' ? 'numeric' : kind === 'number' ? 'decimal' : undefined}
              value={typeof value === 'string' ? value : ''}
              onChange={(e) => onChange(e.target.value)}
            />
          )}
        </FieldShell>
      )
    }
    case 'boolean': {
      // Optional booleans with no default get a tri-state so "not sent" stays possible.
      if (!required && schema.default === undefined) {
        return (
          <FieldShell id={id} label={label} required={false} description={description} error={error}>
            <select
              {...common}
              value={value === true ? 'true' : value === false ? 'false' : ''}
              onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value === 'true')}
            >
              <option value="">Not set</option>
              <option value="true">true</option>
              <option value="false">false</option>
            </select>
          </FieldShell>
        )
      }
      return (
        <FieldShell id={id} label={label} required={required} description={description} error={error} inline>
          <input {...common} type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} />
        </FieldShell>
      )
    }
    case 'object': {
      const obj = (typeof value === 'object' && value !== null && !Array.isArray(value) ? value : {}) as DraftObject
      return (
        <fieldset className="sf-group" aria-describedby={describedBy(id, description, error)}>
          <legend>
            {label}
            {required && <RequiredMark />}
          </legend>
          {description && <p id={`${id}-desc`} className="sf-help">{description}</p>}
          <Fields schema={schema} value={obj} onChange={onChange} path={path} errors={errors} idPrefix={idPrefix} />
        </fieldset>
      )
    }
    case 'array': {
      const items = Array.isArray(value) ? value : []
      const item = itemSchema(schema)
      return (
        <fieldset className="sf-group" aria-describedby={describedBy(id, description, error)}>
          <legend>
            {label}
            {required && <RequiredMark />}
            <span className="sf-type"> list</span>
          </legend>
          {description && <p id={`${id}-desc`} className="sf-help">{description}</p>}
          {items.length === 0 && <p className="sf-help">No items.</p>}
          {items.map((it, i) => (
            <div className="sf-item" key={i}>
              <Field
                name={`${name} ${i + 1}`}
                schema={item}
                value={it}
                onChange={(v) => onChange(items.map((x, j) => (j === i ? v : x)))}
                path={`${path}[${i}]`}
                required
                errors={errors}
                idPrefix={idPrefix}
              />
              <button
                type="button"
                className="btn btn--sm"
                aria-label={`Remove ${name} ${i + 1}`}
                onClick={() => onChange(items.filter((_, j) => j !== i))}
              >
                Remove
              </button>
            </div>
          ))}
          <button type="button" className="btn btn--sm" onClick={() => onChange([...items, initialDraft(item)])}>
            Add {name} item
          </button>
          {error && <p id={`${id}-err`} className="sf-error">{error}</p>}
        </fieldset>
      )
    }
    case 'json':
      return (
        <FieldShell id={id} label={`${label} (JSON)`} required={required} description={description} error={error}>
          <textarea
            {...common}
            className="sf-json"
            rows={4}
            spellCheck={false}
            placeholder="{}"
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(e.target.value)}
          />
        </FieldShell>
      )
  }
}

function Fields({
  schema,
  value,
  onChange,
  path,
  errors,
  idPrefix,
}: {
  schema: JsonSchema
  value: DraftObject
  onChange: (v: DraftObject) => void
  path: string
  errors: FieldErrors
  idPrefix: string
}) {
  const req = requiredOf(schema)
  // Required fields first, keeping schema order within each group.
  const props = propertiesOf(schema).sort(([a], [b]) => Number(req.has(b)) - Number(req.has(a)))
  return (
    <>
      {props.map(([k, ps]) => (
        <Field
          key={k}
          name={k}
          schema={ps}
          value={value[k]}
          onChange={(v) => onChange({ ...value, [k]: v })}
          path={path ? `${path}.${k}` : k}
          required={req.has(k)}
          errors={errors}
          idPrefix={idPrefix}
        />
      ))}
    </>
  )
}

/** Renders a form for a tool's JSON-Schema `input_schema`. Controlled via a Draft tree. */
export function SchemaForm({
  schema,
  value,
  onChange,
  errors,
  idPrefix,
}: {
  schema: JsonSchema
  value: Draft
  onChange: (v: Draft) => void
  errors: FieldErrors
  idPrefix: string
}) {
  if (kindOf(schema) !== 'object') {
    return <p className="muted sf-none">This tool takes no arguments.</p>
  }
  const obj = (typeof value === 'object' && value !== null && !Array.isArray(value) ? value : {}) as DraftObject
  return (
    <div className="sf">
      <Fields schema={schema} value={obj} onChange={onChange} path="" errors={errors} idPrefix={idPrefix} />
    </div>
  )
}
